import {
  SendWorkflowNotificationSMSRequest,
  WorkflowNotificationResource,
  WorkflowsService,
} from '@/api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useStore } from '@nanostores/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Clock9, Loader2, MessageSquare, Send } from 'lucide-react';
import { toast } from 'sonner';

import React from 'react';

export interface SMS {
  id: string;
  threadId?: string | null;
  author: string;
  date: string;
  body: string;
  read: boolean;
  messages?: SMS[]; // if this email is actually a thread, all messages are stored here
  resources?: WorkflowNotificationResource[];
}

export interface SMSDetailProps {
  sms: SMS | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SMSDetail: React.FC<SMSDetailProps> = ({ sms, open, onOpenChange }) => {
  if (!sms) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center text-gray-500"></div>
    );
  }

  // If this is a thread, use all messages; otherwise, a single message.
  const conversation =
    sms.messages && sms.messages.length > 0 ? sms.messages : [sms];

  // Sort conversation (oldest at the top)
  const sortedConversation = conversation.sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );
  const org = useStore($org)?.id;
  const queryClient = useQueryClient();
  const [reply, setReply] = React.useState('');

  const sendSms = useMutation({
    mutationFn: async (payload: SendWorkflowNotificationSMSRequest) => {
      // call the api to create the organization
      const res = await WorkflowsService.sendWorkflowNotificationSms(payload);
      return res;
    },
    onSuccess: async data => {
      // invalidate queries here so that they are refetched
      await queryClient.invalidateQueries({
        queryKey: ['getNotifications', org, undefined, 'SMS_NOTIFICATION'],
      });
      setReply('');
      toast.success('Message sent successfully.');
    },
    onError: error => {
      toast.error('Failed to send message.');
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal>
      <DialogContent className="flex max-h-[90vh] max-w-[650px] flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <div className="mr-2">
              <MessageSquare className="h-5 w-5 text-green-500" />
            </div>
            <div className="w-[200px] truncate">{sms.body}</div>
          </DialogTitle>
          <DialogDescription>
            <div className="mt-1 flex items-center text-xs text-gray-500">
              <Clock9 className="mr-1 h-3 w-3" />
              {new Date(sms.date).toLocaleDateString()}{' '}
              {new Date(sms.date).toLocaleTimeString()}
            </div>
          </DialogDescription>
        </DialogHeader>
        <div className="flex h-[100%] flex-col overflow-y-auto px-2">
          {/* Conversation Thread */}

          {sortedConversation.map((msg, index) => (
            <div key={msg.id} className="mb-3 rounded-md border">
              <div className="rounded-lg bg-white p-4 shadow-sm">
                <div className="mb-4">
                  <div className="flex flex-col space-y-1 text-sm text-muted-foreground">
                    <div className="flex items-center justify-between gap-x-1">
                      <div>
                        <span className="font-medium">From</span>
                        <span className="ml-1 max-w-[100px] break-words">
                          {msg.author}
                        </span>
                      </div>
                      <div className="flex items-center whitespace-nowrap text-xs text-gray-500">
                        <Clock9 className="mr-1 h-3 w-3" />
                        {new Date(msg.date).toLocaleDateString()}{' '}
                        {new Date(msg.date).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                </div>
                <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-gray-800">
                  {msg.body}
                </p>
                {msg.resources && msg.resources.length > 0 && (
                  <div className="mt-4 flex flex-col gap-2">
                    <p className="text-sm font-medium text-gray-600">
                      Attachments:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {msg.resources.map((resource, index) => (
                        <a
                          key={index}
                          href={resource.downloadUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-600 transition-colors hover:bg-blue-100 hover:text-blue-800"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                            />
                          </svg>
                          {resource.fileName}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter className="flex w-full">
          <div className="flex w-full flex-col gap-2">
            <Textarea
              placeholder="Type your message here."
              rows={3}
              className="w-full"
              value={reply}
              onChange={e => setReply(e.target.value)}
            />

            <div className="flex justify-end">
              {/* <TooltipProvider delayDuration={1}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button className="" variant="outline" size="sm">
                  <Sparkles className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="bg-slate-900">
                <p>Generate reply with AI</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider> */}

              <Button
                variant="default"
                size="sm"
                disabled={sendSms.isPending}
                onClick={async () => {
                  if (
                    !sortedConversation[sortedConversation?.length - 1].threadId
                  ) {
                    return toast.error('Thread ID is empty.');
                  }
                  await sendSms.mutateAsync({
                    threadId:
                      sortedConversation[sortedConversation?.length - 1]
                        .threadId!,
                    messageBody: reply,
                  });
                }}
              >
                {sendSms.isPending ? (
                  <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                ) : (
                  <Send className="mr-2 h-3 w-3" />
                )}
                Send
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SMSDetail;
